import React from 'react';
import { Button, Table, InputNumber, Input, DatePicker } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { FG111M } from '../../../services/pwg201Api';
import dayjs from 'dayjs';

interface Props {
  details: FG111M[];
  isEditing: boolean;
  onChange: (rows: FG111M[]) => void;
}

const DetailsTab: React.FC<Props> = React.memo(({ details, isEditing, onChange }) => {
  const addRow = () => {
    onChange([
      ...details,
      {
        fg111_01: '',
        fg111_02: details.length + 1,
        fg111_11: '',
        fg111_12: '',
        fg111_13: '',
        fg111_14: '',
        fg111_15: '',
        fg111_16: '',
        fg111_17: '',
        fg111_171: '',
        fg111_18: null,
        fg111_19: 'NTD',
        fg111_20: 0,
        fg111_21: 1,
        fg111_22: '',
        fg111_23: '',
        fg111_24: 0,
        fg111_25: '',
        fg111_26: null,
        fg111_27: null,
        fg111_28: '',
        fg111_29: 0,
        fg111_30: '',
        fg111_31: '',
        fg111_32: 0,
        fg111_34: '',
        fg111_90: 0,
      },
    ]);
  };

  const deleteRow = (idx: number) => {
    onChange(details.filter((_, i) => i !== idx));
  };

  const updateRow = (idx: number, field: keyof FG111M, value: any) => {
    const updated = [...details];
    (updated[idx] as any)[field] = value;
    onChange(updated);
  };

  const columns: ColumnsType<FG111M> = [
    { title: <span data-contract-field="fg111_02">序</span>, dataIndex: 'fg111_02', width: 50 },
    {
      title: <span data-contract-field="fg111_11">應收帳款單號</span>,
      dataIndex: 'fg111_11',
      width: 120,
      render: (v, _, i) =>
        isEditing ? (
          <Input size="small" value={v} onChange={e => updateRow(i, 'fg111_11', e.target.value)} />
        ) : (
          v
        ),
    },
    {
      title: <span data-contract-field="fg111_19">幣別</span>,
      dataIndex: 'fg111_19',
      width: 70,
      render: (v, _, i) =>
        isEditing ? (
          <Input size="small" value={v} maxLength={3} onChange={e => updateRow(i, 'fg111_19', e.target.value)} />
        ) : (
          v
        ),
    },
    {
      title: <span data-contract-field="fg111_20">原幣金額</span>,
      dataIndex: 'fg111_20',
      width: 110,
      render: (v, _, i) =>
        isEditing ? (
          <InputNumber
            size="small"
            value={v}
            style={{ width: '100%' }}
            onChange={val => updateRow(i, 'fg111_20', val ?? 0)}
          />
        ) : (
          v
        ),
    },
    {
      title: <span data-contract-field="fg111_21">匯率</span>,
      dataIndex: 'fg111_21',
      width: 80,
      render: (v, _, i) =>
        isEditing ? (
          <InputNumber
            size="small"
            value={v}
            style={{ width: '100%' }}
            onChange={val => updateRow(i, 'fg111_21', val ?? 1)}
          />
        ) : (
          v
        ),
    },
    {
      title: <span data-contract-field="fg111_29">本幣金額</span>,
      dataIndex: 'fg111_29',
      width: 110,
    },
    {
      title: <span data-contract-field="fg111_18">到期日</span>,
      dataIndex: 'fg111_18',
      width: 120,
      render: (v, _, i) =>
        isEditing ? (
          <DatePicker
            size="small"
            value={v ? dayjs(v) : null}
            style={{ width: '100%' }}
            onChange={d => updateRow(i, 'fg111_18', d ? d.format('YYYY-MM-DD') : null)}
          />
        ) : (
          v
        ),
    },
    {
      title: <span data-contract-field="fg111_30">備註</span>,
      dataIndex: 'fg111_30',
      render: (v, _, i) =>
        isEditing ? (
          <Input size="small" value={v} onChange={e => updateRow(i, 'fg111_30', e.target.value)} />
        ) : (
          v
        ),
    },
    ...(isEditing
      ? [
          {
            title: '',
            width: 40,
            render: (_: any, __: FG111M, i: number) => (
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                size="small"
                onClick={() => deleteRow(i)}
              />
            ),
          },
        ]
      : []),
  ];

  return (
    <div>
      <Table
        size="small"
        columns={columns}
        dataSource={details}
        rowKey={(r, i) => `detail-${i}`}
        pagination={false}
        scroll={{ x: 700 }}
      />
      {isEditing && (
        <Button icon={<PlusOutlined />} onClick={addRow} style={{ marginTop: 8 }}>
          新增明細
        </Button>
      )}
    </div>
  );
});

export default DetailsTab;
